import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Catches React render errors so the app doesn't unmount to a blank page.
 * Shows the user a friendly recovery UI with the actual error text (so
 * staff can screenshot it) and a one-click reload.
 *
 * Note: error boundaries only catch errors during render, in lifecycle
 * methods, and in constructors — they do NOT catch errors in event
 * handlers, async code (promises, setTimeout), or server-side rendering.
 * Those still need try/catch around the call site.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console so the dev/staff can inspect or screenshot.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-lg w-full rounded-lg border bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
            <div>
              <h1 className="font-semibold text-lg">Something went wrong</h1>
              <p className="text-sm text-muted-foreground">
                The page hit an unexpected error. Reloading usually fixes it.
              </p>
            </div>
          </div>

          <details className="text-xs bg-muted rounded p-3 mb-4 font-mono overflow-auto max-h-64">
            <summary className="cursor-pointer mb-2 select-none">Error details</summary>
            <div className="whitespace-pre-wrap break-words">
              <div className="font-semibold mb-1">{this.state.error.name}: {this.state.error.message}</div>
              {this.state.error.stack && (
                <pre className="text-[10px] opacity-70 mt-2">{this.state.error.stack}</pre>
              )}
              {this.state.errorInfo?.componentStack && (
                <>
                  <div className="font-semibold mt-3 mb-1">Component stack:</div>
                  <pre className="text-[10px] opacity-70">{this.state.errorInfo.componentStack}</pre>
                </>
              )}
            </div>
          </details>

          <div className="flex gap-2">
            <Button onClick={this.handleReload} className="flex-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
